import { useState } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import instance from '@api/axiosInstance';
import { selectedCustomerIdSelector } from '@store/common/selectors';
import { intervalSelector, filterSelector } from '@store/overviewDashboard/base/selectors';
import { DATASET } from '@src/utils/constants';

interface DimensionMetricsParams {
  dimension: string;
  metrics: string[];
}

interface DimensionMetricsResponse {
  data: any[];
  error: string | null;
}

export const useDimensionMetrics = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const interval = useSelector(intervalSelector);
  const accountId = useSelector(selectedCustomerIdSelector);
  const appliedFilter = useSelector(filterSelector);

  const fetchDimensionMetrics = async ({
    dimension,
    metrics,
  }: DimensionMetricsParams): Promise<DimensionMetricsResponse> => {
    setLoading(true);
    setError(null);

    try {
      const filterRule = get(appliedFilter, 'rule', []);

      const query = {
        type: 'group-by',
        dataset: DATASET,
        account: accountId,
        interval: `${interval.from}/${interval.to}`,
        granularity: 'ALL',
        filter: filterRule,
        groupBy: [dimension],
        metrics,
        sortBy: [{ metric: metrics[0], order: 'desc' }],
      };

      const response = await instance.post('/metrics', { queries: [query] });
      const results = get(response, ['data', 0, 'data', 0, 'results'], []);
      setLoading(false);
      return { data: results, error: null };
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || err?.message || 'Failed to fetch dimension metrics';
      setError(errorMessage);
      setLoading(false);
      return { data: [], error: errorMessage };
    }
  };

  return {
    fetchDimensionMetrics,
    loading,
    error,
  };
};
